<template>
  <div class="min-h-screen">
    <!-- header with primary background -->
    <div class="bg-primary px-6 pt-8 pb-16">
      <div class="max-w-4xl mx-auto">
        <!-- page title -->
        <h1 class="text-white text-2xl font-bold mb-6">Profile</h1>

        <!-- user info row -->
        <div class="flex items-center gap-4">
          <!-- avatar -->
          <div
            class="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"
          >
            <ImgComponent
              :src="$stores.user?.state?.current?.email"
              avatar
              alt="Profile"
              class="w-full h-full rounded-full"
            />
          </div>
          <!-- name and email -->
          <div class="flex-1 min-w-0">
            <p class="text-white font-semibold truncate">
              {{ $stores.user?.state?.current?.firstName }}
              {{ $stores.user?.state?.current?.lastName }}
            </p>
            <p class="text-white/70 text-sm truncate">
              {{ $stores.user?.state?.current?.email }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- main content card -->
    <div class="-mt-12">
      <div class="bg-white rounded-4xl overflow-hidden p-4">
        <!-- settings section -->
        <div>
          <div
            class="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Paramètres
          </div>
          <div
            class="flex items-center gap-4 px-5 py-4 active:bg-gray-50 cursor-pointer transition-colors"
            v-bus:EVENTS.BOOKMAKER.SELECT_BOOKMAKER_ID_TO_CHANGE.click
          >
            <ColorImg
              src="/images/profile/setting.svg"
              class="w-5 h-5 bg-gray-700"
            />
            <span class="flex-1 font-medium text-gray-900"
              >Changer mes IDs</span
            >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9CA3AF"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        <!-- help section -->
        <div>
          <div
            class="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Aide
          </div>
          <div
            v-for="(content, i) in helpContents"
            :key="i"
            @click="
              () => {
                if (content.id == 'rechargeHelp') rechargeHelpClick();
                if (content.id == 'withdrawHelp') withdrawHelpClick();
              }
            "
            class="flex items-center gap-4 px-5 py-4 active:bg-gray-50 cursor-pointer transition-colors"
          >
            <ColorImg :src="content.icon" class="w-5 h-5 bg-gray-700" />
            <span class="flex-1 font-medium text-gray-900">{{
              content.name
            }}</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9CA3AF"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        <!-- contact section -->
        <div>
          <div
            class="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Contact
          </div>
          <a
            v-for="(content, i) in contactContents"
            :key="i"
            :href="
              (content.id == 'email' ? 'mailto:' : '') +
              ($stores.appData?.state?.data?.general?.[content.id] || '')
            "
            target="_blank"
            class="flex items-center gap-4 px-5 py-4 active:bg-gray-50 transition-colors block"
          >
            <ColorImg :src="content.icon" class="w-5 h-5 bg-gray-700" />
            <span class="flex-1 font-medium text-gray-900">{{
              content.name
            }}</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9CA3AF"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <!-- social networks section -->
        <div>
          <div
            class="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Réseaux sociaux
          </div>
          <a
            v-for="(content, i) in networksContents"
            :key="i"
            :href="$stores.appData?.state?.data?.general?.[content.id] || ''"
            target="_blank"
            class="flex items-center gap-4 px-5 py-4 active:bg-gray-50 transition-colors block"
          >
            <ColorImg :src="content.icon" class="w-5 h-5 bg-gray-700" />
            <span class="flex-1 font-medium text-gray-900">{{
              content.name
            }}</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9CA3AF"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <!-- logout section -->
        <div>
          <div
            @click="
              () => {
                $bus.emit($constants.EVENTS.USER.LOGOUT);
                $router.replace('/login');
              }
            "
            class="flex items-center gap-4 px-5 py-4 active:bg-gray-50 cursor-pointer transition-colors"
          >
            <ColorImg
              src="/images/profile/logout.svg"
              class="w-5 h-5 bg-red-600"
            />
            <span class="flex-1 font-medium text-red-600">Me déconnecter</span>
          </div>
        </div>
      </div>
    </div>

    <div class="mobile-bottom-padding"></div>
  </div>

  <!-- hidden link for opening content -->
  <div class="hidden">
    <a
      :href="requestedContentLink"
      ref="requestedContentLinkTag"
      target="_blank"
    ></a>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ProfilePage',
  setup() {
    return {};
  },
  data() {
    return {
      requestedContentLink: '',
      contactContents: [
        {
          id: 'whatsappAssistanceLink',
          name: 'Whatsapp',
          icon: '/images/profile/whatsapp.svg',
          actionText: 'Contacter'
        },
        {
          id: 'telegramAssistanceLink',
          name: 'Telegram',
          icon: '/images/profile/telegram.svg',
          actionText: 'Contacter'
        },
        {
          id: 'email',
          name: 'Email',
          icon: '/images/profile/email.svg',
          actionText: 'Envoyer'
        },
        {
          id: 'faqUrl',
          name: 'F.A.Q.',
          icon: '/images/profile/help.svg',
          actionText: 'Visiter'
        }
      ],
      networksContents: [
        {
          id: 'whatsappLink',
          name: 'Whatsapp',
          icon: '/images/profile/whatsapp.svg',
          actionText: 'Suivre'
        },
        {
          id: 'telegramLink',
          name: 'Telegram',
          icon: '/images/profile/telegram.svg',
          actionText: 'Suivre'
        },
        {
          id: 'instagramLink',
          name: 'Instagram',
          icon: '/images/profile/instagram_1.svg',
          actionText: 'Suivre'
        },
        {
          id: 'facebookLink',
          name: 'Facebook',
          icon: '/images/profile/facebook-app-symbol.svg',
          actionText: 'Suivre'
        },
        {
          id: 'tiktokLink',
          name: 'TikTok',
          icon: '/images/profile/tik-tok.svg',
          actionText: 'Suivre'
        },
        {
          id: 'websiteLink',
          name: 'Site Web',
          icon: '/images/profile/website.svg',
          actionText: 'Visiter'
        }
      ],
      helpContents: [
        {
          id: 'rechargeHelp',
          isLink: false,
          name: 'Comment faire un dépôt ?',
          icon: '/images/profile/help.svg',
          actionText: 'Voir'
        },
        {
          id: 'withdrawHelp',
          isLink: false,
          name: 'Comment faire un retrait ?',
          icon: '/images/profile/help.svg',
          actionText: 'Voir'
        }
      ]
    };
  },
  methods: {
    rechargeHelpClick() {
      this.requestContentDisplay(
        'Comment faire un dépôt via 1xPay',
        'rechargeVideoLink',
        'rechargeTutorialLink'
      );
    },
    withdrawHelpClick() {
      this.requestContentDisplay(
        'Comment faire des retrait via 1xPay',
        'withdrawVideoLink',
        'withdrawTutorialLink'
      );
    },
    async requestContentDisplay(title, videoKey, tutoKey) {
      const contentToShow = await this.requestTypeOfHelpToShow(title);
      if (!contentToShow) return;
      let requestedContentLink = null;
      if (contentToShow == 'video')
        requestedContentLink =
          this.$stores.appData?.state?.data?.general?.[videoKey];
      if (contentToShow == 'tutorial')
        requestedContentLink =
          this.$stores.appData?.state?.data?.general?.[tutoKey];
      if (!requestedContentLink)
        this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Contenu non disponible',
          body: "Ce contenu n'est pas disponible pour le moment"
        });
      else {
        this.requestedContentLink = requestedContentLink;
        setTimeout(() => {
          this.$refs.requestedContentLinkTag.click();
        }, 200);
      }
    },
    async requestTypeOfHelpToShow(title) {
      const acocuntIdsPreferencesConfig = [
        {
          title: 'Guide documentaire',
          name: 'tutorial'
        },
        {
          title: 'Vidéo explicative',
          name: 'video'
        }
      ];
      const prompt = await window.choicePrompt({
        title: title,
        options: acocuntIdsPreferencesConfig,
        formatDisplay: (data) => data?.title || '',
        successText: 'Continuer'
      });
      if (!prompt.accepted || !prompt.data) return null;
      return prompt.data.name;
    }
  }
});
</script>
